import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import {
  AppErrorScreen,
  NotFoundScreen,
} from "@/features/funnel/components/fallback-screens";
import { ensureSession } from "@/shared/lib/ensure-session";
import "./app.css";

// Apply initial theme (dark is the product default — the funnel is a
// night-time surface by design).
const theme = localStorage.getItem("secret-missions-theme") || "dark";
document.documentElement.classList.toggle("dark", theme === "dark");

// In dev, Vite serves the SPA at `/`. In prod, Django will mount it at
// `/secretmissions/` — TanStack Router needs to know that prefix or the
// root route won't match. Detect at runtime by sniffing the pathname so
// the same build artifact works wherever it's mounted (TCM pattern).
const SPA_BASEPATH = window.location.pathname.startsWith("/secretmissions")
  ? "/secretmissions"
  : undefined;

const router = createRouter({
  routeTree,
  basepath: SPA_BASEPATH,
  // In-chrome fallbacks: a mistyped/stale URL or a render-time crash must
  // land on the funnel's own screens, never TanStack Router's bare
  // defaults — this app's whole job is not losing visitors.
  defaultNotFoundComponent: NotFoundScreen,
  defaultErrorComponent: AppErrorScreen,
});

// Register router for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

async function startApp() {
  // Funnel fixtures can coexist with the real local chat API. The webchat
  // handler bypasses only its own namespace when VITE_WEBCHAT_REAL_API=true.
  // VITE_ENABLE_MOCKS=false still disables all mocks for ordinary backend dev.
  if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_MOCKS !== "false") {
    const { worker } = await import("./mocks/browser");
    await worker.start({
      onUnhandledRequest: "bypass",
      serviceWorker: {
        url: "/mockServiceWorker.js",
      },
    });
  }

  // New visitor? Mint a temporary member + session before we render, so the
  // first session-bound call already resolves to a member. No-op if a session
  // already exists.
  if (
    !/^\/(?:secretmissions\/)?(?:misionbankeditor|b|beta|beta-admin)\/?$/.test(
      window.location.pathname,
    )
  ) {
    await ensureSession();
  }

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
}

startApp();
